// =========================================================
// ブラウザ側の SSR hydrator。
// SSR 済み HTML (data-ssr-key を持つ要素) を検出し、隣の <script> から props を取り出して
// ReactDOM.hydrate する。frontend/packs/*.jsx が末尾で renderReact(name, Component) を呼ぶ形。
// =========================================================

// WARNING: DOM 走査に使う data-ssr-* 属性名は
// サーバー側 ssrServer.js / PlaceholderHtml と密結合。変更不可。

import React from 'react';
import ReactDOM from 'react-dom';
import * as Sentry from '@sentry/react';
import initSentryOnce from './sentryInit';

// NOTE: hydrate失敗は全PVで一斉発生しうるため、Sentryクォータ保護として10%サンプリングで送る (アラート閾値は1/10前提)。
const SENTRY_SAMPLE_RATE = 0.1;

function reportHydrateError(err) {
  if (Math.random() >= SENTRY_SAMPLE_RATE) return;
  // NOTE: ErrorBoundary を通らない pack では Sentry が未初期化のため
  initSentryOnce();
  Sentry.captureException(err, { tags: { ssr_hydrate: true } });
}

export function renderReact(name, Component) {
  if (typeof window === 'undefined') {
    return Component;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => hydrateAll(name, Component));
  } else {
    hydrateAll(name, Component);
  }

  return Component;
}

function hydrateAll(name, Component) {
  // NOTE: サーバー側が data-ssr-key に入れる際の \W 除去と揃える。
  const key = String(name).replace(/\W/g, '');
  const containers = document.querySelectorAll(`div[data-ssr-key="${cssEscape(key)}"]`);
  containers.forEach((container) => hydrateOne(container, key, Component));
}

function hydrateOne(container, key, Component) {
  const id = container.getAttribute('data-ssr-id');
  if (!id) return;

  const script = document.querySelector(
    `script[type="application/json"][data-ssr-key="${cssEscape(key)}"][data-ssr-id="${cssEscape(id)}"]`,
  );
  if (!script) return;

  const props = parseProps(script.textContent);
  if (props === null) return;

  try {
    ReactDOM.hydrate(React.createElement(Component, props), container);
  } catch (err) {
    console.error(`SSR hydrate failed for ${key}:`, err);
    reportHydrateError(err);
  }
}

function parseProps(raw) {
  if (!raw) return null;
  try {
    return JSON.parse(raw.trim());
  } catch (e) {
    console.error('SSR hydrate: invalid JSON', e);
    reportHydrateError(e);
    return null;
  }
}

// NOTE: 古いブラウザ向けに window.CSS.escape のフォールバックを用意。
function cssEscape(value) {
  if (typeof window !== 'undefined' && window.CSS && typeof window.CSS.escape === 'function') {
    return window.CSS.escape(value);
  }
  return String(value).replace(/(["\\])/g, '\\$1');
}

export default { renderReact };
